import { graphql } from '@/__generated__';
import { useCacheMonitor } from '@/context/DiscoveryUrqlProvider';
import { usePaginatedQuery } from '@/reducers/usePaginatedQuery';
import { parseUniqueId } from '@/typings/parseUniqueId';

type Result = ReturnType<typeof useRecentResourceChanges>;

export type RecentResourceChangeResponse = NonNullable<Result['data']>[number];

export const GetRecentResourceChanges = graphql(/* GraphQL */ `
  query RecentResourceChanges($environmentId: BigInt!, $first: Int, $after: String) {
    environment(id: $environmentId) {
      applied {
        lastUpdatedAt
        recentResourceChanges(first: $first, numDays: 7, after: $after) {
          totalCount
          pageInfo {
            endCursor
            hasNextPage
          }
          edges {
            node {
              changes
              mostRecentChangedAt
              uniqueId
              resource {
                resourceType
                filePath
                ... on ModelAppliedStateNestedNode {
                  access
                  name
                  group
                }
              }
            }
          }
        }
      }
    }
  }
`);

type RecentResourceChangesParams = {
  pageSize: number;
  environmentId: number | undefined;
  pause?: boolean;
};

export const useRecentResourceChanges = ({
  pageSize,
  environmentId = 0,
  pause,
}: RecentResourceChangesParams) => {
  const query = usePaginatedQuery(
    {
      query: GetRecentResourceChanges,
      variables: {
        first: pageSize,
        after: undefined,
        environmentId,
      },
      pause: pause || !environmentId,
    },
    (data) => {
      const recentResourceChanges = data.environment.applied?.recentResourceChanges;
      const pageInfo = recentResourceChanges?.pageInfo;
      if (recentResourceChanges == null || pageInfo == null) return undefined;
      return {
        totalCount: recentResourceChanges.totalCount ?? null,
        endCursor: pageInfo.endCursor ?? null,
        hasNextPage: pageInfo.hasNextPage ?? false,
      };
    },
    (data) =>
      data.environment.applied?.recentResourceChanges?.edges.map((edge) => {
        const parsedUniqueId = parseUniqueId(edge.node.uniqueId);
        return {
          ...edge.node,
          resource: edge.node.resource ?? {
            resourceType: parsedUniqueId.resourceType,
            name: parsedUniqueId.remainder,
            access: null,
            filePath: null,
            group: null,
          },
        };
      }),
    (data) => data.environment.applied?.lastUpdatedAt,
  );

  useCacheMonitor({ definition: query.lastUpdatedAt });

  return query;
};
